import { UserAgent, Web } from 'sip.js'
import { SessionState } from 'sip.js/lib/api/session-state'
import type {
  SimpleUser as SipSimpleUser,
  SimpleUserDelegate,
} from 'sip.js/lib/platform/web'
import type {
  SoftphoneCredentials,
  SoftphoneIceServer,
} from '@/types/api/softphone'

export interface SoftphoneSipClientEvents {
  onRegistered?: () => void
  onUnregistered?: () => void
  onServerConnect?: () => void
  onServerDisconnect?: (error?: Error) => void
  onIncomingCall?: (identity: SoftphoneIncomingCallIdentity) => void
  onCallCreated?: () => void
  onCallAnswered?: () => void
  onCallHangup?: () => void
  onCallHold?: (held: boolean) => void
  onError?: (error: Error) => void
}

export interface SoftphoneIncomingCallIdentity {
  displayName?: string
  uri?: string
  user?: string
  phoneNumber?: string
}

type SipIdentity = {
  displayName?: string
  uri?: {
    user?: string
    toString: () => string
  }
}

type SipSessionIdentitySource = {
  assertedIdentity?: SipIdentity
  remoteIdentity?: SipIdentity
}

export const getSipSessionCallerIdentity = (
  session?: SipSessionIdentitySource
): SoftphoneIncomingCallIdentity => {
  const identity = session?.assertedIdentity || session?.remoteIdentity
  const user = identity?.uri?.user?.trim()
  const displayName = identity?.displayName?.trim()
  const uri = identity?.uri?.toString().trim()
  const phoneNumber = user && /^\+?\d{7,}$/.test(user) ? user : undefined

  return {
    displayName: displayName || undefined,
    uri: uri || undefined,
    user: user || undefined,
    phoneNumber,
  }
}

const getIceServers = (
  credentials: SoftphoneCredentials
): SoftphoneIceServer[] | undefined =>
  credentials.ice_servers || credentials.iceServers

const REGISTER_EXPIRES_SECONDS = 300
const REGISTRATION_RETRY_SECONDS = 3
const EARLY_INVITE_REJECT_WINDOW_MS = 4000
const isMissingSessionError = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.toLowerCase().includes('session does not exist')
const isSessionAlreadyAnsweringError = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.toLowerCase().includes('invalid session state establishing')

const getSipUri = (credentials: SoftphoneCredentials): string =>
  `sip:${credentials.username}@${credentials.sip_domain}`

export const getCallCorrelationHeaders = (callId?: string): string[] => {
  const normalizedCallId = callId?.trim()

  return normalizedCallId && /^[A-Za-z0-9-]+$/.test(normalizedCallId)
    ? [`X-ERA-Call-ID: ${normalizedCallId}`]
    : []
}

export const getSipCredentialsConnectionKey = (
  credentials?: SoftphoneCredentials
): string | null => {
  if (!credentials) return null

  const iceServers = getIceServers(credentials)?.map(server => ({
    urls: server.urls,
    username: server.username,
    credential: server.credential,
  }))

  return JSON.stringify([
    credentials.username,
    credentials.password,
    credentials.sip_domain,
    credentials.wss_uri,
    iceServers,
  ])
}

const getRegistrarUri = (credentials: SoftphoneCredentials) => {
  const registrar = UserAgent.makeURI(`sip:${credentials.sip_domain}`)

  if (!registrar) {
    throw new Error('Unable to create softphone SIP registrar URI')
  }

  return registrar
}

export const resolveSipDestination = (
  dialTarget: string,
  credentials: SoftphoneCredentials
): string => {
  const trimmedTarget = dialTarget.trim()

  if (/^sips?:/i.test(trimmedTarget)) {
    return trimmedTarget
  }

  const pjsipMatch = trimmedTarget.match(/^PJSIP\/(.+)$/i)
  const endpoint = pjsipMatch?.[1] || trimmedTarget

  if (endpoint.includes('@')) {
    return `sip:${endpoint}`
  }

  return `sip:${endpoint}@${credentials.sip_domain}`
}

export class SoftphoneSipClient {
  private simpleUser: SipSimpleUser | null = null
  private credentials: SoftphoneCredentials | null = null
  private answerPromise: Promise<void> | null = null

  constructor(
    private readonly remoteAudio: HTMLAudioElement,
    private readonly events: SoftphoneSipClientEvents = {}
  ) {}

  async connect(credentials: SoftphoneCredentials): Promise<void> {
    await this.disconnect()
    this.credentials = credentials
    this.prepareRemoteAudio()

    const delegate: SimpleUserDelegate = {
      onRegistered: () => this.events.onRegistered?.(),
      onUnregistered: () => this.events.onUnregistered?.(),
      onServerConnect: () => this.events.onServerConnect?.(),
      onServerDisconnect: error => this.events.onServerDisconnect?.(error),
      onCallReceived: () => {
        const session = (
          this.simpleUser as unknown as {
            session?: SipSessionIdentitySource
          }
        )?.session
        this.events.onIncomingCall?.(getSipSessionCallerIdentity(session))
      },
      onCallCreated: () => this.events.onCallCreated?.(),
      onCallAnswered: () => {
        this.playRemoteAudio()
        this.events.onCallAnswered?.()
      },
      onCallHangup: () => this.events.onCallHangup?.(),
      onCallHold: held => this.events.onCallHold?.(held),
    }

    this.simpleUser = new Web.SimpleUser(credentials.wss_uri, {
      aor: getSipUri(credentials),
      delegate,
      media: {
        constraints: { audio: true, video: false },
        remote: {
          audio: this.remoteAudio,
        },
      },
      reconnectionAttempts: 5,
      reconnectionDelay: 4,
      registererOptions: {
        expires: REGISTER_EXPIRES_SECONDS,
        registrar: getRegistrarUri(credentials),
        extraContactHeaderParams: ['transport=ws', 'ob'],
        params: {
          fromDisplayName: 'ERA Dispatcher',
          optionTags: ['outbound', 'path'],
        },
        refreshFrequency: 90,
      },
      userAgentOptions: {
        authorizationUsername: credentials.username,
        authorizationPassword: credentials.password,
        displayName: 'ERA Dispatcher',
        logBuiltinEnabled: false,
        uri: UserAgent.makeURI(getSipUri(credentials)),
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: getIceServers(credentials),
          },
        },
      },
      registrationRetry: true,
      registrationRetryInterval: REGISTRATION_RETRY_SECONDS,
    } as Web.SimpleUserOptions)

    await this.simpleUser.connect()
    await this.simpleUser.register()
  }

  async disconnect(): Promise<void> {
    if (!this.simpleUser) return

    const currentUser = this.simpleUser
    this.simpleUser = null

    try {
      await currentUser.unregister()
    } catch (error) {
      this.events.onError?.(error as Error)
    }

    try {
      await currentUser.disconnect()
    } catch (error) {
      this.events.onError?.(error as Error)
    }
  }

  isConnected(): boolean {
    return Boolean(this.simpleUser?.isConnected())
  }

  async call(dialTarget: string, callId?: string): Promise<void> {
    if (!this.simpleUser || !this.credentials) {
      throw new Error('Softphone is not connected')
    }

    this.prepareRemoteAudio()
    const simpleUser = this.simpleUser
    const destination = resolveSipDestination(dialTarget, this.credentials)

    await new Promise<void>((resolve, reject) => {
      let settled = false
      let rejectTimer: number | undefined

      const settle = (handler: () => void) => {
        if (settled) return
        settled = true
        if (rejectTimer) window.clearTimeout(rejectTimer)
        handler()
      }

      simpleUser
        .call(
          destination,
          { extraHeaders: getCallCorrelationHeaders(callId) },
          {
            requestDelegate: {
              onAccept: () => settle(resolve),
              onReject: response => {
                const statusCode = response.message.statusCode
                const reasonPhrase = response.message.reasonPhrase
                const reasonHeader = response.message.getHeader('Reason')
                const message = [
                  'SIP INVITE rejected',
                  statusCode,
                  reasonPhrase,
                  reasonHeader,
                ]
                  .filter(Boolean)
                  .join(' ')

                const error = new Error(message) as Error & {
                  sipStatusCode?: number
                  sipReasonPhrase?: string
                  sipReasonHeader?: string
                }
                error.sipStatusCode = statusCode
                error.sipReasonPhrase = reasonPhrase
                error.sipReasonHeader = reasonHeader

                settle(() => reject(error))
              },
            },
          }
        )
        .then(() => {
          rejectTimer = window.setTimeout(
            () => settle(resolve),
            EARLY_INVITE_REJECT_WINDOW_MS
          )
        })
        .catch(callError => settle(() => reject(callError)))
    })
  }

  async answer(): Promise<void> {
    if (!this.simpleUser) {
      throw new Error('Softphone is not connected')
    }

    if (this.answerPromise) {
      return this.answerPromise
    }

    const sessionState = this.getSessionState()
    if (sessionState === SessionState.Establishing) {
      return
    }
    if (sessionState === SessionState.Established) {
      this.playRemoteAudio()
      return
    }

    this.prepareRemoteAudio()
    this.answerPromise = this.simpleUser
      .answer()
      .catch(error => {
        if (isSessionAlreadyAnsweringError(error)) return
        throw error
      })
      .finally(() => {
        this.answerPromise = null
      })

    await this.answerPromise
  }

  async decline(): Promise<void> {
    if (!this.simpleUser) return
    try {
      await this.simpleUser.decline()
    } catch (error) {
      if (!isMissingSessionError(error)) throw error
    }
  }

  async hangup(): Promise<void> {
    if (!this.simpleUser) return
    try {
      await this.simpleUser.hangup()
    } catch (error) {
      if (!isMissingSessionError(error)) throw error
    }
  }

  async hold(): Promise<void> {
    if (!this.simpleUser) {
      throw new Error('Softphone is not connected')
    }

    await this.simpleUser.hold()
  }

  async unhold(): Promise<void> {
    if (!this.simpleUser) {
      throw new Error('Softphone is not connected')
    }

    await this.simpleUser.unhold()
  }

  mute(): void {
    this.simpleUser?.mute()
  }

  unmute(): void {
    this.simpleUser?.unmute()
  }

  isMuted(): boolean {
    return Boolean(this.simpleUser?.isMuted())
  }

  isHeld(): boolean {
    return Boolean(this.simpleUser?.isHeld())
  }

  playRemoteAudio(): void {
    this.prepareRemoteAudio()
    void this.remoteAudio.play().catch(error => {
      const message =
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'Call audio was blocked by the browser. Click the call controls again to enable sound.'
          : error instanceof Error
            ? error.message
            : 'Unable to play call audio'

      this.events.onError?.(new Error(message))
    })
  }

  private prepareRemoteAudio(): void {
    this.remoteAudio.autoplay = true
    this.remoteAudio.muted = false
    this.remoteAudio.volume = 1
    this.remoteAudio.setAttribute('playsinline', 'true')
  }

  private getSessionState(): SessionState | undefined {
    const simpleUser = this.simpleUser as {
      session?: { state?: SessionState }
    } | null

    return simpleUser?.session?.state
  }
}
